import { useEffect, useRef, useState } from 'react'
import type { DealPartyRoster } from '@/services/dealParties'
import { fact, partyName } from './partyViewModel'
import { buildMapGraph, type Node } from './mapGraph'
import { PartyDialog } from './PartyDialog'

type Props = {
  roster: DealPartyRoster
  onSelect: (id: string) => void
  selectedId?: string
  onClose?: () => void
  embedded?: boolean
  /** An organisation node no roster entry stands for — a firm a person is
   *  recorded at — opens the organisation's own view. */
  onOpenOrganization?: (name: string) => void
}
function short(value: string, max = 27) {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value
}

/** Existing SVG renderer, with recorded organization membership and keyboard selection. */
export function RelationshipMap({
  roster,
  onSelect,
  selectedId,
  onClose,
  embedded = false,
  onOpenOrganization,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [aspect, setAspect] = useState(1.9)
  useEffect(() => {
    const svg = svgRef.current
    if (!svg || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      if (width > 0 && height > 0) setAspect(width / height)
    })
    observer.observe(svg)
    return () => observer.disconnect()
  }, [])
  const { nodes, links } = buildMapGraph(roster)
  const activate = (node: Node) => {
    if (node.partyIds.length) {
      onSelect(node.partyIds.includes(selectedId ?? '') ? selectedId! : node.partyIds[0])
    } else if (node.organization) {
      onOpenOrganization?.(node.label)
    }
  }
  // Fit the complete roster in two banks with a clear central deal corridor.
  let columnsPerSide = 1
  let bestScale = 0
  for (
    let columns = 1;
    columns <= Math.max(1, Math.ceil(nodes.length / 2));
    columns++
  ) {
    const candidateHeight = Math.max(
      190,
      Math.ceil(nodes.length / (columns * 2)) * 86 + 24,
    )
    const scale = Math.min(aspect / (columns * 412 + 150), 1 / candidateHeight)
    if (scale > bestScale) {
      bestScale = scale
      columnsPerSide = columns
    }
  }
  const rows = Math.max(1, Math.ceil(nodes.length / (columnsPerSide * 2)))
  const width = columnsPerSide * 412 + 150
  const height = Math.max(190, rows * 86 + 24)
  const centerX = width / 2
  const centerY = height / 2
  nodes.forEach((node, index) => {
    const column = Math.floor(index / 2) % columnsPerSide
    const row = Math.floor(index / (columnsPerSide * 2))
    node.x = index % 2 === 0 ? 103 + column * 206 : centerX + 178 + column * 206
    node.y = (height - (rows - 1) * 86) / 2 + row * 86
  })
  const graph = (
    <div className="pi-map-content">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="group"
        aria-label="Parties connected to this deal"
      >
        {nodes
          .filter((n) => n.onDeal)
          .map((node) => {
            // The label sits midway along the part of the edge nobody draws
            // over — between the deal's circle and the node's box — so the
            // role never lands on a box.
            const dx = node.x - centerX
            const dy = node.y - centerY
            const length = Math.hypot(dx, dy) || 1
            const ux = dx / length
            const uy = dy / length
            const intoBox = Math.min(
              ux ? 91 / Math.abs(ux) : Infinity,
              uy ? 31 / Math.abs(uy) : Infinity,
            )
            const visible = length - 51 - intoBox
            const along = 51 + Math.max(visible, 0) / 2
            const labelX = centerX + ux * along
            const labelY = centerY + uy * along
            const labelText = short(node.roles.join(' / ') || node.role, 36)
            // An outer node's edge runs under the inner node beside it; a
            // role drawn there is hidden by (or reads as belonging to) that
            // box. The node itself still shows the role, so the edge label
            // is simply not drawn where it would land on another node.
            const halfWidth = labelText.length * 2.6
            const landsOnNode = nodes.some(
              (other) =>
                other !== node &&
                Math.abs(labelX - other.x) < 91 + halfWidth &&
                Math.abs(labelY - 4 - other.y) < 31 + 6,
            )
            return (
              <g key={`deal-${node.id}`}>
                <line
                  x1={centerX}
                  y1={centerY}
                  x2={node.x}
                  y2={node.y}
                  stroke={node.color}
                  strokeOpacity={
                    node.partyIds.includes(selectedId ?? '') ? 0.9 : 0.4
                  }
                  strokeDasharray={node.organization ? '5 4' : undefined}
                >
                  <title>Role on this deal: {node.roles.join(', ') || node.role}</title>
                </line>
                <text
                  x={labelX}
                  y={labelY - 4}
                  textAnchor="middle"
                  fill={node.color}
                  fontSize="9"
                  stroke="#0b0b0b"
                  strokeWidth="3"
                  paintOrder="stroke"
                  className="pi-map-edge-label"
                  visibility={visible >= 40 && !landsOnNode ? 'visible' : 'hidden'}
                >
                  {labelText}
                </text>
              </g>
            )
          })}
        {links.map((link) => {
          const from = nodes.find((n) => n.id === link.from)
          const to = nodes.find((n) => n.id === link.to)
          return from && to ? (
            <line
              key={`${link.from}-${link.to}`}
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              stroke={from.color}
              strokeOpacity=".5"
              strokeDasharray="4 5"
            >
              <title>{link.title}</title>
            </line>
          ) : null
        })}
        {roster.parties.map((party) => {
          const introducer = fact(party, 'Introduced by')
          const source = nodes.find(
            (node) => !node.organization && node.label === introducer,
          )
          const target = nodes.find((node) => node.partyIds.includes(party.id))
          return source && target && source.id !== target.id ? (
            <line
              key={`introduced-${party.id}`}
              x1={source.x}
              y1={source.y}
              x2={target.x}
              y2={target.y}
              stroke="#d9b768"
              strokeDasharray="2 5"
            >
              <title>
                {introducer} introduced {partyName(party)} · recorded party fact
              </title>
            </line>
          ) : null
        })}
        <circle
          cx={centerX}
          cy={centerY}
          r="51"
          fill="#0b0b0b"
          stroke="#d9b768"
        />
        <text
          x={centerX}
          y={centerY - 9}
          textAnchor="middle"
          fill="#f2eee5"
          fontSize="13"
        >
          Deal
        </text>
        <text
          x={centerX}
          y={centerY + 13}
          textAnchor="middle"
          fill="#d9b768"
          fontSize="12"
        >
          {short(roster.loanId)}
        </text>
        {nodes.map((node) => (
          <g
            key={node.id}
            role="button"
            tabIndex={0}
            aria-pressed={node.partyIds.includes(selectedId ?? '')}
            aria-label={`${node.organization ? 'Organization' : node.role}: ${node.label}`}
            onClick={() => activate(node)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                activate(node)
              }
            }}
            className="pi-map-node"
          >
            <title>
              {node.label} · {node.detail} · {node.role}
            </title>
            <rect
              x={node.x - 91}
              y={node.y - 31}
              width="182"
              height="62"
              rx="5"
              fill={
                node.partyIds.includes(selectedId ?? '') ? '#171717' : '#0b0b0b'
              }
              stroke={node.color}
              strokeDasharray={node.organization ? '5 3' : undefined}
              strokeWidth={node.partyIds.includes(selectedId ?? '') ? 2 : 1}
            />
            <text
              x={node.x}
              y={node.y - 11}
              textAnchor="middle"
              fill="#f4f3ef"
              fontSize="12"
            >
              {short(node.label)}
            </text>
            <text
              x={node.x}
              y={node.y + 5}
              textAnchor="middle"
              fill="#aab4bf"
              fontSize="10"
            >
              {short(node.detail)}
            </text>
            <text
              x={node.x}
              y={node.y + 21}
              textAnchor="middle"
              fill={node.color}
              fontSize="11"
            >
              {short(node.role)}
            </text>
          </g>
        ))}
      </svg>
      <div className="pi-map-legend">
        <span style={{ color: '#d9b768' }}>● Sponsor / Borrower</span>
        <span style={{ color: '#64b7ee' }}>● Intermediary</span>
        <span style={{ color: '#58c68b' }}>● Internal team</span>
        <span>— Person / deal</span>
        <span>┄ Organization</span>
      </div>
    </div>
  )
  return embedded ? (
    graph
  ) : (
    <PartyDialog title="Relationship map" onClose={onClose ?? (() => {})}>
      {graph}
    </PartyDialog>
  )
}
